import React from "react";
import { cn } from "@/lib/utils";

/**
 * Ô thông tin chung của màn chi tiết (chuẩn 02/11/2026): nhãn ở trên, giá trị ở dưới, giá trị dài xuống dòng (không cắt "…").
 * Độ rộng theo loại dữ liệu, một quy tắc chung cho mọi màn:
 * - short  : mã, ngày, số lượng, trạng thái, tiền — 1 ô lưới
 * - medium : khách hàng, sản phẩm, lệnh SX, NCC, người, kho… (link chứng từ có tên) — 2 ô lưới
 * - full   : địa chỉ, ghi chú, quy cách, mô tả, lý do — cả dòng
 * Không truyền `size` thì suy ra từ nhãn (DETAIL_FIELD_RULES). Lưới chứa ô tự lấp chỗ trống (index.css: data-detail-field).
 * Thay cho các hàm F riêng của từng màn (cùng props: mono, hl, link, className, valueClass, icon, colSpan).
 */
export type DetailFieldSize = "short" | "medium" | "full";

export const DETAIL_FIELD_RULES: Array<[RegExp, DetailFieldSize]> = [
  [/địa chỉ|ghi chú|mô tả|quy cách|lý do|nội dung|yêu cầu|diễn giải|đặc tả|kết luận|nhận xét|ghi nhận|hướng dẫn/i, "full"],
  // Nhãn mở đầu bằng mã / loại / số / ngày / trạng thái là giá trị ngắn dù có chữ "khách hàng", "sản phẩm"… (VD Mã khách hàng)
  [/^(mã|loại|hạng|nhóm|số|ngày|hạn|trạng thái|tổng|sl|đơn giá|giá|đvt|đơn vị|ca|tỷ|phương thức|dung sai|điện thoại|sđt|chức vụ|cỡ|mức)(?=\s|$)/i, "short"],
  // Tên người (người kiểm, thợ, NV kinh doanh, liên hệ) ngắn nên giữ 1 ô; "máy" chỉ khi nhãn bắt đầu bằng Máy (không gồm "Thợ đứng máy")
  [/khách hàng|sản phẩm|lệnh sx|lệnh sản xuất|nhà cung cấp|\bncc\b|đơn hàng|đơn mua|báo giá|vật tư|nguyên liệu|^máy|kho|mẫu|tên sp|tên sản phẩm|tên vật tư|email|công đoạn|po khách|tham chiếu|phiếu|bài bình|khuôn|kẽm|đối tượng/i, "medium"],
];

export function detailFieldSize(label: string): DetailFieldSize {
  const hit = DETAIL_FIELD_RULES.find(([re]) => re.test(label));
  return hit ? hit[1] : "short";
}

const SPAN: Record<DetailFieldSize, string> = {
  short: "",
  medium: "col-span-2", // 2/4 cột trên máy tính, cả hàng trên điện thoại (lưới 2 cột)
  full: "col-span-full",
};

const LEGACY_SPAN: Record<number, string> = {
  1: "",
  2: "col-span-2",
  3: "col-span-2 md:col-span-3",
  4: "col-span-full",
};

export interface DetailFieldProps {
  label: string;
  value?: any;
  mono?: boolean;
  hl?: boolean;
  link?: () => void;
  size?: DetailFieldSize;
  /** Tương thích các F cũ: số ô lưới cố định */
  colSpan?: 1 | 2 | 3 | 4;
  /** Tương thích các F cũ (bỏ qua: nhãn luôn ở trên) */
  col?: boolean;
  icon?: React.ElementType;
  className?: string;
  valueClass?: string;
}

function renderValue(value: any) {
  if (value === undefined || value === null || value === "") return <span className="text-slate-300 font-normal">—</span>;
  if (typeof value === "string" || typeof value === "number") return String(value);
  if (typeof value === "object" && !React.isValidElement(value) && !Array.isArray(value)) {
    return String(value?.name || value?.label || value?.code || value?.id || "—");
  }
  return value;
}

export function DetailField({ label, value, mono, hl, link, size, colSpan, icon: Icon, className, valueClass }: DetailFieldProps) {
  const span = colSpan ? LEGACY_SPAN[colSpan] : SPAN[size || detailFieldSize(label)];
  return (
    <div data-detail-field className={cn("flex flex-col gap-0.5 min-w-0", span, className)}>
      <span className="text-slate-500 text-[11px] leading-tight flex items-center gap-1">
        {Icon ? <Icon className="w-3 h-3 shrink-0" /> : null}
        {label}
      </span>
      <div
        className={cn(
          "text-[13px] leading-snug break-words min-w-0",
          mono ? "font-mono text-[12.5px] font-semibold text-slate-800" : "font-semibold text-slate-800",
          hl && "text-indigo-600 font-bold",
          link && "text-indigo-600 cursor-pointer hover:underline",
          valueClass,
        )}
        onClick={link}
      >
        {renderValue(value)}
      </div>
    </div>
  );
}

export default DetailField;
